import { Link } from 'react-router-dom';
import { ArrowRight, Car, CheckCircle2, Clock3, Layers, ShieldCheck, ThumbsUp } from 'lucide-react';
import { Button } from '../components/ui/button';
import SEO from '../components/SEO';
import { PORTAL_LINKS } from '../constants/site';

const BENEFITS = [
  { title: 'Access to multiple lenders', text: 'Compare options across a broad lender panel instead of relying on a single provider.', icon: Layers },
  { title: 'Faster approvals', text: 'Structured submissions reduce delays and get your application moving sooner.', icon: Clock3 },
  { title: 'We handle the process', text: 'We manage lender matching, follow-up, and paperwork from start to finish.', icon: ShieldCheck },
  { title: 'Better chance of approval', text: 'We position your file properly before submission to improve approval outcomes.', icon: ThumbsUp },
];

const PROCESS_STEPS = [
  'Tell us your goals and preferred vehicle or loan outcome.',
  'We compare lender policies and shortlist suitable options.',
  'We package your file and guide you through approval to settlement.',
];

export default function CarLoansPage() {
  return (
    <div className="min-h-screen pt-24" data-testid="car-loans-page">
      <SEO
        title="Car Loans & Personal Finance Australia | Flash Funding"
        description="Get matched with the right car loan or personal loan lender in Australia. Flash Funding handles the process end to end. Fast, simple, no credit score impact."
        keywords="car loans, car finance australia, used car finance, new car loans"
        canonical="/car-loans"
      />

      <section className="py-16 sm:py-20" data-testid="car-loans-hero-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl rounded-3xl border border-purple-200 bg-white p-8 shadow-xl shadow-purple-200/30 sm:p-10" data-testid="car-loans-hero-card">
            <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-purple-200 bg-purple-50 px-4 py-2" data-testid="car-loans-hero-badge">
              <Car size={14} className="text-purple-700" />
              <span className="text-xs uppercase tracking-[0.14em] text-purple-700">Car Loans</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.04] text-[#0F0A1A]" data-testid="car-loans-hero-heading">
              Car Loans &amp; Personal Finance — Matched to Your Situation
            </h1>
            <p className="mt-5 max-w-3xl text-sm text-[#4A4556] sm:text-base" data-testid="car-loans-hero-subheading">
              Get matched with lenders that fit your profile and vehicle type, with support through the full process.
            </p>

            <div className="mt-7 flex flex-wrap gap-3" data-testid="car-loans-hero-cta-row">
              <a href={PORTAL_LINKS.CAR} data-testid="car-loans-hero-primary-link">
                <Button className="rounded-full bg-purple-600 px-7 py-6 text-xs font-bold uppercase tracking-[0.14em] text-white hover:bg-purple-700" data-testid="car-loans-hero-primary-cta">
                  Check Your Options
                  <ArrowRight size={15} className="ml-2" />
                </Button>
              </a>
              <a href={PORTAL_LINKS.CAR} data-testid="car-loans-hero-secondary-link">
                <Button variant="outline" className="rounded-full border-purple-300 bg-purple-50 px-7 py-6 text-xs font-bold uppercase tracking-[0.14em] text-purple-700 hover:bg-purple-100" data-testid="car-loans-hero-secondary-cta">
                  See What You Can Get Approved For
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      <section className="border-y border-purple-100 bg-white py-14" data-testid="car-loans-clarity-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="car-loans-solution-grid">
            <article className="rounded-2xl border border-purple-200 bg-white p-6" data-testid="car-loans-solution-card-car-loans">
              <h2 className="mb-3 text-3xl font-bold text-[#0F0A1A] sm:text-4xl" data-testid="car-loans-solution-heading-car-loans">Car Loans</h2>
              <p className="text-sm leading-relaxed text-[#4A4556] sm:text-base" data-testid="car-loans-solution-text-car-loans">
                Compare lender options for new and used vehicle purchases with repayments tailored to your monthly budget.
              </p>
            </article>
            <article className="rounded-2xl border border-purple-200 bg-[#faf9fd] p-6" data-testid="car-loans-solution-card-personal-loans">
              <h2 className="mb-3 text-3xl font-bold text-[#0F0A1A] sm:text-4xl" data-testid="car-loans-solution-heading-personal-loans">Personal Loans</h2>
              <p className="text-sm leading-relaxed text-[#4A4556] sm:text-base" data-testid="car-loans-solution-text-personal-loans">
                Access flexible personal lending for life expenses while still getting support from broker-led lender matching.
              </p>
            </article>
          </div>
        </div>
      </section>

      <section className="py-16" data-testid="car-loans-benefits-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="mb-8 text-3xl font-bold text-[#0F0A1A] sm:text-4xl" data-testid="car-loans-benefits-heading">Finance for Challenging Credit Situations</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {BENEFITS.map((item) => (
              <article key={item.title} className="rounded-2xl border border-purple-200 bg-white p-6" data-testid={`car-loans-benefit-card-${item.title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
                <item.icon size={19} className="mb-3 text-purple-700" />
                <h3 className="mb-2 font-semibold text-[#1B1431]" data-testid={`car-loans-benefit-title-${item.title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>{item.title}</h3>
                <p className="text-sm text-[#4A4556]" data-testid={`car-loans-benefit-text-${item.title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>{item.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white py-16" data-testid="car-loans-process-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="mb-8 text-3xl font-bold text-[#0F0A1A] sm:text-4xl" data-testid="car-loans-process-heading">How the Process Works</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="car-loans-process-grid">
            {PROCESS_STEPS.map((step, index) => (
              <article key={step} className="rounded-2xl border border-purple-200 bg-[#faf9fd] p-6" data-testid={`car-loans-process-card-${index + 1}`}>
                <span className="mb-3 inline-flex h-8 w-8 items-center justify-center rounded-full bg-purple-100 text-sm text-purple-700" data-testid={`car-loans-process-number-${index + 1}`}>{index + 1}</span>
                <p className="text-sm text-[#4A4556]" data-testid={`car-loans-process-text-${index + 1}`}>{step}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 sm:py-20" data-testid="car-loans-final-cta-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="mb-4 text-3xl font-bold text-[#0F0A1A] sm:text-4xl" data-testid="car-loans-final-cta-heading">
            Check Your Options in 60 Seconds
          </h2>
          <p className="mb-8 text-sm text-[#4A4556] sm:text-base" data-testid="car-loans-final-cta-subheading">
            Start with a quick guided application and we’ll match you with lender options that suit your profile.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-3" data-testid="car-loans-final-cta-row">
            <a href={PORTAL_LINKS.CAR} data-testid="car-loans-final-primary-link">
              <Button className="w-full rounded-full bg-purple-600 px-8 py-6 text-xs font-bold uppercase tracking-[0.14em] text-white hover:bg-purple-700 sm:w-auto" data-testid="car-loans-final-primary-cta">
                See What You Can Get Approved For
              </Button>
            </a>
            <a href={PORTAL_LINKS.CAR} data-testid="car-loans-final-secondary-link">
              <Button variant="outline" className="w-full rounded-full border-purple-300 bg-purple-50 px-8 py-6 text-xs font-bold uppercase tracking-[0.14em] text-purple-700 hover:bg-purple-100 sm:w-auto" data-testid="car-loans-final-secondary-cta">
                Check Your Options
              </Button>
            </a>
          </div>
          <p className="mt-4 text-xs text-[#665F79]" data-testid="car-loans-final-note">
            <CheckCircle2 size={12} className="mr-1 inline text-purple-700" />
            Takes 60 seconds • No impact on your credit score
          </p>
        </div>
      </section>
    </div>
  );
}
